import React from 'react'
import {
  BlogMajor,
  ChecklistAlternateMajor,
  CustomersMajor,
  FinancesMajor,
  FraudProtectMajor,
  NoteMajor,
  PageDownMajor,
  QuestionMarkMajor,
  SecureMajor,
  ShipmentMajor,
  SmileyHappyMajor,
  TextBlockMajor,
} from '@shopify/polaris-icons'
import { GSIcon } from 'src/components/shared/GSIcon'

export enum CollectedDataStatus {
  Created = 'created',
  InProgress = 'in_progress',
  WaitingForInternalApproval = 'waiting_for_internal_approval',
  WaitingForApproval = 'waiting_for_approval',
  Completed = 'completed',
  CompletedNotActive = 'completed_not_active',
  Failed = 'failed', // Applies only for BG check
  Expired = 'expired',
}

export const isApprovedCollectedDataStatus = (status: CollectedDataStatus): boolean =>
  status === CollectedDataStatus.Completed || status === CollectedDataStatus.CompletedNotActive

export enum DocumentUploadOrigin {
  ManualContractorUpload = 'manual_contractor_upload',
  ManualInternalUpload = 'manual_internal_upload',
  YardstikUpload = 'yardstik_upload',
}

export enum ContractorStepExpirationStatus {
  Valid = 'valid',
  SoonToExpire = 'soon_to_expire',
  Expired = 'expired',
}

export enum CollectedDataStepType {
  DriverLicense = 'driver_license',
  VehicleAndInsurance = 'vehicle_and_insurance',
  InsuranceAmountDeclaration = 'insurance_amount_declaration',
  VehicleRegistration = 'vehicle_registration',
  VaccinationInformation = 'vaccination_information',
  BackgroundCheck = 'background_check',
  PaymentMethod = 'payment_method',
  InsuranceBeneficiary = 'insurance_beneficiary',
  SignContract = 'sign_contract',
  Training = 'training',
  Questionnaire = 'questionnaire',
  MvrUpload = 'mvr_upload',
  InPersonMeeting = 'in_person_meeting',
  Form = 'form',
  BusinessEntity = 'business_entity',
  InsuranceCoverage = 'insurance',
  LmsTraining = 'lms_training',
  MCNumber = 'mc_number',
  DOTNumber = 'dot_number',
  PIICollection = 'ssn_and_address',
}

export const collectedDataStepsTitle = {
  [CollectedDataStepType.Questionnaire]: 'Questionnaire',
  [CollectedDataStepType.Form]: '',
  [CollectedDataStepType.DriverLicense]: "Driver's License",
  [CollectedDataStepType.VehicleAndInsurance]: 'Vehicle and Insurance',
  [CollectedDataStepType.InsuranceAmountDeclaration]: 'Insurance Amount Declaration',
  [CollectedDataStepType.VehicleRegistration]: 'Vehicle Registration',
  [CollectedDataStepType.VaccinationInformation]: 'Vaccination Information',
  [CollectedDataStepType.BackgroundCheck]: 'Background Check',
  [CollectedDataStepType.PaymentMethod]: 'Settlement Method',
  [CollectedDataStepType.InsuranceBeneficiary]: 'Insurance Beneficiary',
  [CollectedDataStepType.SignContract]: 'Sign the Contract',
  [CollectedDataStepType.Training]: 'Training',
  [CollectedDataStepType.MvrUpload]: 'MVR Upload',
  [CollectedDataStepType.InPersonMeeting]: 'In-person Meeting',
  [CollectedDataStepType.BusinessEntity]: 'Business Entity',
  [CollectedDataStepType.InsuranceCoverage]: 'Insurance Coverage',
  [CollectedDataStepType.LmsTraining]: 'Certification Step',
  [CollectedDataStepType.MCNumber]: 'Motor Carrier Number',
  [CollectedDataStepType.DOTNumber]: 'DOT/USDOT Number',
  [CollectedDataStepType.PIICollection]: 'Social Security Number',
}

export const collectedDataStepIconsMap = {
  [CollectedDataStepType.Questionnaire]: <QuestionMarkMajor width={20} />,
  [CollectedDataStepType.Form]: <ChecklistAlternateMajor width={20} />,
  [CollectedDataStepType.DriverLicense]: <GSIcon iconName="gs_driver_front" />,
  [CollectedDataStepType.VehicleAndInsurance]: <ShipmentMajor width={20} />,
  [CollectedDataStepType.InsuranceAmountDeclaration]: <NoteMajor height={20} />,
  [CollectedDataStepType.VehicleRegistration]: <TextBlockMajor height={20} width={20} />,
  [CollectedDataStepType.VaccinationInformation]: <SecureMajor height={20} width={20} />,
  [CollectedDataStepType.BackgroundCheck]: <GSIcon iconName="gs_bgc" />,
  [CollectedDataStepType.PaymentMethod]: <FinancesMajor height={20} width={20} />,
  [CollectedDataStepType.InsuranceBeneficiary]: <CustomersMajor height={18} width={16} />,
  [CollectedDataStepType.SignContract]: <BlogMajor height={20} width={19} />,
  [CollectedDataStepType.Training]: <GSIcon iconName="gs_bookmark" />,
  [CollectedDataStepType.MvrUpload]: <PageDownMajor width={20} />,
  [CollectedDataStepType.InPersonMeeting]: <SmileyHappyMajor height={20} width={20} />,
  [CollectedDataStepType.BusinessEntity]: <NoteMajor height={20} width={20} />,
  [CollectedDataStepType.InsuranceCoverage]: <FraudProtectMajor height={20} width={20} />,
  [CollectedDataStepType.LmsTraining]: <NoteMajor height={20} width={20} />,
  [CollectedDataStepType.MCNumber]: <NoteMajor height={20} width={20} />,
  [CollectedDataStepType.DOTNumber]: <ShipmentMajor width={20} />,
  [CollectedDataStepType.PIICollection]: <TextBlockMajor height={20} width={20} />,
}

export enum CollectDataFormItemType {
  TextBlock = 'textBlock',
  TextInput = 'textInput',
  Date = 'date',
  Number = 'number',
  Select = 'select',
  Radio = 'radio',
  Checkbox = 'checkbox',
  Video = 'video',
  File = 'file',
}

export enum CollectDataStepDeclineReason {
  NotOwnerOfInsurance = 'not_owner_of_insurance',
  DeclinedByCustomer = 'declined_by_customer',
}

export enum BackgroundCheckDataStatus {
  Created = 'created',
  Pending = 'pending',
  Dispute = 'dispute',
  InfoRequested = 'info_requested',
  Consider = 'consider',
  PreAdverse = 'pre_adverse',
  FinalAdverse = 'final_adverse',
}

export const backgroundCheckDataStatusTitles = {
  [BackgroundCheckDataStatus.Created]: 'Created',
  [BackgroundCheckDataStatus.Pending]: 'Pending',
  [BackgroundCheckDataStatus.Dispute]: 'Dispute',
  [BackgroundCheckDataStatus.InfoRequested]: 'Info requested',
  [BackgroundCheckDataStatus.Consider]: 'Consider',
  [BackgroundCheckDataStatus.PreAdverse]: 'Pre adverse',
  [BackgroundCheckDataStatus.FinalAdverse]: 'Final adverse',
}

export const getBackgroundCheckReminderText = (
  dataStatus: BackgroundCheckDataStatus,
  stepStatus: CollectedDataStatus,
): string => {
  if (
    dataStatus === BackgroundCheckDataStatus.Created &&
    stepStatus === CollectedDataStatus.Created
  ) {
    return "Contractor has started this step but has not finished. You can remind them to complete it by clicking 'Resend an invite' at the bottom left."
  }

  if (dataStatus === BackgroundCheckDataStatus.Dispute) {
    return 'Most disputes are resolved within 30 days but some may take up to 45 days depending on the information provided.'
  }

  if (
    dataStatus === BackgroundCheckDataStatus.Created ||
    dataStatus === BackgroundCheckDataStatus.InfoRequested
  ) {
    return "You can remind the contractor to complete this step by clicking the 'Resend an invite' button at the bottom left."
  }

  if (
    dataStatus === BackgroundCheckDataStatus.PreAdverse ||
    dataStatus === BackgroundCheckDataStatus.FinalAdverse
  ) {
    return 'The contractor received a "pre-adverse" email from Yardstik. They should follow the instructions in that email or contact Yardstik at support@yardstik.com for assistance if they want to dispute results.'
  }

  return ''
}

export enum CollectedDataApprovalType {
  AutoApproval = 'auto_approval',
  InternalApproval = 'internal_approval',
  CustomerApproval = 'customer_approval',
  InternalThenCustomerApproval = 'internal_then_customer_approval',
  CustomerThenInternalApproval = 'customer_then_internal_approval',
}

export const collectedDataStatusTitle = {
  [CollectedDataStatus.Created]: 'Not Started',
  [CollectedDataStatus.InProgress]: 'In progress',
  [CollectedDataStatus.WaitingForInternalApproval]: 'Pending',
  [CollectedDataStatus.WaitingForApproval]: 'Waiting for approval',
  [CollectedDataStatus.Completed]: 'Approved',
  [CollectedDataStatus.CompletedNotActive]: 'Approved',
  [CollectedDataStatus.Failed]: 'Failed',
  [CollectedDataStatus.Expired]: 'Expired',
}
